import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Permission {
  name: string;
  read: boolean;
  write: boolean;
}

interface Functionality {
  name: string;
  enabled: boolean;
  description: string;
}

const AddNewRolePage: React.FC = () => {
  const navigate = useNavigate();

  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');

  const [functionalities, setFunctionalities] = useState<Functionality[]>([
    { name: 'User Management', enabled: false, description: 'Manage platform users and their access' },
    { name: 'Campaign Creation', enabled: false, description: 'Create and manage SMS campaigns' },
    { name: 'Template Management', enabled: false, description: 'Create and manage message templates' },
    { name: 'Billing Access', enabled: false, description: 'Access billing and payment information' },
    { name: 'Report Generation', enabled: false, description: 'Generate and view analytics reports' },
    { name: 'Group Management', enabled: false, description: 'Create and manage contact groups' },
    { name: 'System Settings', enabled: false, description: 'Configure platform settings' },
  ]);

  const [permissions, setPermissions] = useState<Permission[]>([
    { name: 'User Management', read: false, write: false },
    { name: 'Messaging', read: false, write: false },
    { name: 'Templates', read: false, write: false },
    { name: 'Billing', read: false, write: false },
    { name: 'Reports', read: false, write: false },
  ]);

  const handleFunctionalityToggle = (index: number) => {
    const updatedFunctionalities = [...functionalities];
    updatedFunctionalities[index].enabled = !updatedFunctionalities[index].enabled;
    setFunctionalities(updatedFunctionalities);
  };

  const handlePermissionChange = (index: number, type: 'read' | 'write') => {
    const updatedPermissions = [...permissions];
    updatedPermissions[index][type] = !updatedPermissions[index][type];
    setPermissions(updatedPermissions);
  };

  const handleSave = () => {
    // Implement save functionality
    console.log('Saving role...', { roleName, roleDescription, functionalities, permissions });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-medium">Add New Role</h1>
        <div className="space-x-3">
          <button
            onClick={() => navigate('/superadmin/users-roles')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
          >
            Save Role
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Role Information */}
        <div>
          <h2 className="text-lg font-medium mb-4">Role Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role Name
              </label>
              <input
                type="text"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g., Regional Manager"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={roleDescription}
                onChange={(e) => setRoleDescription(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Describe the role's responsibilities..."
              />
            </div>
          </div>
        </div>

        {/* Functionalities */}
        <div>
          <h2 className="text-lg font-medium mb-4">Role Functionalities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {functionalities.map((functionality, index) => (
              <div 
                key={functionality.name}
                className="border rounded-lg p-4 hover:bg-gray-50"
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={functionality.enabled}
                    onChange={() => handleFunctionalityToggle(index)}
                    className="mt-1 h-4 w-4 text-[#FF5722] rounded border-gray-300 focus:ring-[#FF5722]"
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-900">
                      {functionality.name}
                    </label>
                    <p className="text-sm text-gray-500">{functionality.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Permissions */}
        <div>
          <h2 className="text-lg font-medium mb-4">Permission Rights</h2>
          <div className="space-y-4">
            {permissions.map((permission, index) => (
              <div 
                key={permission.name}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <span className="font-medium">{permission.name}</span>
                <div className="space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={permission.read}
                      onChange={() => handlePermissionChange(index, 'read')}
                      className="h-4 w-4 text-[#FF5722] rounded border-gray-300 focus:ring-[#FF5722]"
                    />
                    <span className="ml-2 text-sm text-gray-600">Read</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={permission.write}
                      onChange={() => handlePermissionChange(index, 'write')}
                      className="h-4 w-4 text-[#FF5722] rounded border-gray-300 focus:ring-[#FF5722]"
                    />
                    <span className="ml-2 text-sm text-gray-600">Write</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewRolePage; 